import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CheckCircle2, XCircle, Loader2, RefreshCw, UserCog, Timer } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CleanupLog from "./CleanupLog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function LastRun({ run }) {
  if (!run) return <span className="text-xs text-muted-foreground" data-testid="cron-last-none">Nog niet gedraaid</span>;
  const Icon = run.status === "error" ? XCircle : run.status === "running" ? Loader2 : CheckCircle2;
  const cls = run.status === "error" ? "text-destructive" : run.status === "running" ? "text-secondary" : "text-positive";
  return (
    <div className="text-xs">
      <div className={`inline-flex items-center gap-1 font-bold ${cls}`}>
        <Icon className={`w-3.5 h-3.5 ${run.status === "running" ? "animate-spin" : ""}`} />
        {fmt(run.received_at)}
        <span className="ml-1 px-1 py-0.5 bg-muted text-muted-foreground text-[10px] tracking-widest uppercase inline-flex items-center gap-1">
          {run.source === "admin" ? <><UserCog className="w-3 h-3" /> handmatig</> : <><Timer className="w-3 h-3" /> cron</>}
        </span>
      </div>
      <div className="text-muted-foreground mt-0.5 break-words">{run.summary}{run.triggered_by ? ` · door ${run.triggered_by}` : ""}</div>
    </div>
  );
}

export default function CronLogBlock() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get("/admin/cron-status")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Laden mislukt."); }
    setBusy(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow overflow-x-auto" data-testid="cron-log-block">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">Geplande taken</h2>
          <p className="text-xs text-muted-foreground mt-1">Laatste run en volgende geplande run van de automatische taken (platform-cron).</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="cron-log-refresh"
          className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Ververs
        </button>
      </div>
      {!data ? <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div> : (
        <table className="w-full text-sm" data-testid="cron-log-table">
          <thead>
            <tr className="bg-foreground text-background">
              {["Taak", "Schema", "Laatste run", "Volgende run"].map((h) => (
                <th key={h} className="px-3 py-2 font-display uppercase text-xs tracking-widest text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.jobs.map((j) => (
              <tr key={j.job} data-testid={`cron-job-${j.job}`} className="border-t border-border align-top">
                <td className="px-3 py-3">
                  <div className="font-bold">{j.label}</div>
                  <div className="text-xs text-muted-foreground">{j.description}</div>
                </td>
                <td className="px-3 py-3 text-xs"><CalendarClock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />{j.schedule}</td>
                <td className="px-3 py-3" data-testid={`cron-last-${j.job}`}><LastRun run={j.last_run} /></td>
                <td className="px-3 py-3 text-xs font-mono-num" data-testid={`cron-next-${j.job}`}>{fmt(j.next_run)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <CleanupLog />
    </section>
  );
}
